import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

const resources = {
  en: {
    translation: {
      appName: 'TreeRated10',
      landing: {
        title: 'Rate Your Christmas Tree with AI',
        subtitle: 'Take a photo and get an instant rating',
        cameraButton: 'Open Camera',
        galleryButton: 'Choose from Gallery',
        quizButton: 'Discover Your 2025 Style',
        login: 'Login',
      },
      howItWorks: {
        title: 'How It Works',
        subtitle: 'Rate your Christmas tree in 3 simple steps',
        step1Title: 'Take a Photo',
        step1Desc: 'Use your camera or choose from gallery',
        step2Title: 'AI Analyzes',
        step2Desc: 'Our AI evaluates decoration, lighting, and trends',
        step3Title: 'Get Your Score',
        step3Desc: 'Receive rating and personalized tips',
      },
      trends: {
        badge: '2025 TRENDS',
        title: 'Discover Your Christmas Style',
        subtitle: 'Which 2025 trend matches your personality?',
        minimalist: 'Minimalist',
        rustic: 'Rustic',
        tech: 'Tech',
        glam: 'Glamorous',
        cta: 'Take the Style Quiz',
        learnMore: 'Learn more about 2025 trends',
      },
      trendsPage: {
        meta: {
          title: 'Christmas Tree Decoration Trends 2025 - Complete Guide | TreeRated10',
          description: 'Discover the main Christmas tree decoration trends for 2025: Scandinavian minimalism, rustic style, smart technology, and glamorous maximalism. Complete guide with tips, colors and materials.',
          keywords: 'christmas tree trends 2025, christmas decoration, scandinavian minimalism, rustic christmas, smart led lights, christmas tree colors, decoration materials',
        },
        header: {
          back: 'Back',
        },
        hero: {
          badge: '2025 TRENDS',
          title: 'Complete Guide to Christmas Tree Decoration 2025',
          subtitle: 'Discover the styles, colors and materials that will dominate this season and transform your tree into a work of art',
          readTime: '📖 Reading time: 8 minutes',
        },
        toc: {
          title: '📑 Table of Contents',
        },
        tips: {
          title: 'Practical Tips',
        },
        colors: {
          neutral: 'Neutral',
          gold: 'Gold',
          green: 'Green',
          red: 'Red',
          blue: 'Blue',
        },
        sections: {
          minimalist: {
            title: 'Scandinavian Minimalism',
            description: 'Cozy minimalism takes center stage in 2025 decorations. Less is more: trees gain few ornaments, but selected with intention and high visual impact, valuing empty space and simple shapes. This style, influenced by Scandinavian design, bets on neutral tones (white, beige, off-white) and natural elements to create an elegant and welcoming environment. Warm lights and organic materials like light wood and fibers provide comfort, avoiding visual overload. Each detail counts: quality ornaments are well spaced, allowing each piece to shine individually. The result is a clean and sophisticated tree that conveys calm without losing Christmas magic.',
            tips: [
              'Use neutral tones like white, beige and soft gray',
              'Choose few high-quality ornaments with intention',
              'Prefer warm LED lights (yellowish) for cozy atmosphere',
              'Add natural materials like light wood and organic fibers',
              'Leave negative space - don\'t overload the tree',
            ],
          },
          rustic: {
            title: 'Rustic & Handmade Style',
            description: 'Parallel to minimalism, there is a strong trend of rustic and artisan decoration, aligned with the search for connections with nature and sustainability. Natural textures continue to rise: wood ornaments, pinecones, dry branches, jute bows and even dried flowers inserted in the tree bring rustic and welcoming charm to the environment. This style rescues countryside symbols and handmade, perfectly combining with boho-inspired interiors or country houses. The appreciation of artisan is one of the biggest trends for Christmas 2025 - many consumers choose to create their own ornaments or purchase handmade pieces, adding a unique and affective touch to decoration.',
            tips: [
              'Add natural elements like pinecones and dry branches',
              'Use jute, linen and natural fiber bows',
              'Include handmade ornaments (crochet, macramé, ceramics)',
              'Mix wood with earthy and green tones',
              'Create unique pieces to tell your story',
            ],
          },
          tech: {
            title: 'Technological & Interactive Decoration',
            description: 'Technological innovation also marks presence in Christmas trees of 2025, elegantly and functionally. Trees gained intelligent LED lighting, with twinkle lights controlled by apps and smart home devices, becoming the great differential of this year. This "high-tech" trend allows programming colors, patterns and custom light rhythms - there are LED light kits that can synchronize with music or be controlled by voice, creating synchronized light shows with the festive environment. Electronic candles with remote control or via app also replace traditional ones, adding brightness and safety without losing cozy atmosphere.',
            tips: [
              'Install smart LED lights with app control',
              'Program colors and light patterns according to occasion',
              'Synchronize lights with Christmas music',
              'Use electronic candles for safety',
              'Add voice control via smart home assistants',
            ],
          },
          glam: {
            title: 'Nostalgic Maximalism & Glamour',
            description: 'In contrast to "less is more" enthusiasts, there is a strong nostalgic maximalism movement that embraces "more is more" with joy. In this trend, decoration returns to exuberant 80s and 90s style: it\'s worth mixing vibrant colors (traditional red and green combined with hot pink, purple, turquoise blue) and many different ornaments occupying every available space on branches. This aesthetic celebrates abundance and fun, referring to childhood\'s loaded trees - full of multicolored lights, garlands, large bows and varied ornaments. Parallel to nostalgic appeal, luxurious glamour remains in evidence for those wanting to show sophistication.',
            tips: [
              'Mix vibrant colors without fear (red, green, pink, purple)',
              'Fill the tree with varied ornaments',
              'Use multicolored lights for festive effect',
              'Add large bows and garlands',
              'Include glitter, crystals and luxurious materials',
            ],
          },
          colors: {
            title: 'Predominant Colors in 2025',
            description: 'Christmas color palettes in 2025 reflect this duality of trends. On one hand, neutral and earthy tones gain prominence in contemporary and minimalist proposals. Soft colors like snow white, beige, off-white, light charcoal and aged gold dominate Scandinavian chic aesthetic. Matte metallic combinations (soft gold, aged copper) with natural tones (moss green, woody brown) are on the rise, providing discreet sophistication. On the other hand, driven by nostalgic maximalism, there is a renaissance of colorful and vibrant palettes this year.',
            tips: [
              'Try pairing neutral tones with metallic accents',
              'If you like color, mix traditional red and green with modern touches',
              'Blue and silver create elegant icy effect',
              'Earthy tones (terracotta, rust) bring cozy rustic vibe',
              'Pastel tones add soft modern touch',
            ],
          },
          materials: {
            title: 'Materials and LED Lighting',
            description: 'The choice of materials for trees and ornaments in 2025 is strongly influenced by the search for quality, sustainability and refined aesthetics. Instead of disposable plastic, consumers are opting for durable and ecologically correct materials. Blown glass, ceramic, aged metal or natural fiber ornaments replace flashy plastic ornaments, aligning decoration with more premium and sustainable aesthetics. Quality fabric ribbons have returned strongly this season, replacing ready-made plastic bows - many magazine decorations display large velvet, satin or linen ribbons decorating pines.',
            sustainable: {
              title: 'Sustainable Materials',
              items: [
                'Blown or hand-painted glass',
                'Ceramic and porcelain',
                'Natural fibers: jute, linen, cotton, felt',
                'Recycled or reclaimed wood',
                'Metals: copper, brass (infinitely recyclable)',
              ],
            },
            lighting: {
              title: 'Smart LED Lighting',
              items: [
                'Smart LED strings with WiFi/Bluetooth',
                'Programmable colors and effects',
                'Synchronization with music',
                'Automatic timers and remote control',
                'Low energy consumption and safe (don\'t heat)',
              ],
            },
          },
          naturalVsArtificial: {
            title: 'Natural vs Artificial Trees',
            description: 'A question that always arises every year is: natural or artificial tree? In 2025, artificial Christmas trees continue to predominate in public preference, but with new concerns guiding this choice. For practicality and long-term economy, many consumers opt for synthetic trees. To serve this market, manufacturers have invested in increasingly realistic and sustainable versions of these products. Today it\'s possible to find artificial pines with foliage extremely faithful to natural and made from less aggressive materials to the environment.',
            natural: {
              title: 'Natural Tree',
              prosLabel: 'Advantages',
              pros: [
                'Authentic fresh pine fragrance',
                'Complete Christmas experience',
                'Natural and unique appearance',
                'Biodegradable and compostable',
              ],
              consLabel: 'Disadvantages',
              cons: [
                'Requires watering and care',
                'Sheds needles',
                'Higher annual cost',
                'Limited availability in some regions',
              ],
            },
            artificial: {
              title: 'Artificial Tree',
              prosLabel: 'Advantages',
              pros: [
                'Reusable for many years',
                'No maintenance required',
                'Long-term economy',
                'Available in various sizes and styles',
                'Some are made from recycled materials',
              ],
              consLabel: 'Disadvantages',
              cons: [
                'Initial manufacturing impact',
                'No natural fragrance',
                'Requires proper storage',
                'Needs to be used 10+ years to compensate environmental impact',
              ],
            },
          },
        },
        cta: {
          title: 'Ready to Discover Your Style?',
          subtitle: 'Take our quiz and find out which 2025 trend matches your personality',
          quiz: 'Discover Your Style',
          evaluate: 'Rate Your Tree',
        },
      },
      socialProof: {
        trusted: 'Trusted by Christmas enthusiasts',
        title: 'Join thousands rating their trees',
        treesRated: 'Trees Rated',
        avgRating: 'Average Rating',
        satisfaction: 'User Satisfaction',
      },
      faq: {
        title: 'Frequently Asked Questions',
        q1: 'How does the AI evaluation work?',
        a1: 'Our AI analyzes your Christmas tree photo considering lighting, ornaments, symmetry, creativity, and 2025 decoration trends. You receive an instant score from 0-10 with personalized feedback.',
        q2: 'Is it free to use?',
        a2: 'Yes! You get one free evaluation to try the service. After that, you can create a free account for more evaluations or upgrade to premium for detailed insights and trend analysis.',
        q3: 'What are premium insights?',
        a3: 'Premium insights include detailed analysis by category (lighting, ornaments, symmetry, creativity), 2025 trends matching your tree, specific improvement suggestions, and comparative ranking with other trees.',
        q4: 'Can I share my tree rating?',
        a4: 'Absolutely! After evaluation, you can share your tree score on Instagram Stories, WhatsApp, and other social networks with a beautiful personalized image.',
        q5: 'What are the 2025 Christmas trends?',
        a5: 'The main 2025 trends are: Contemporary Minimalist (neutral tones, sustainable materials), Rustic & Handmade (natural elements, artisan crafts), Tech & Smart (LED lights with app control), and Glamorous Maximalist (vibrant colors, luxury ornaments).',
      },
      quiz: {
        title: 'Discover Your Christmas Style 2025',
        subtitle: 'Answer 5 questions and discover the perfect trends for you',
        back: 'Back',
        nextButton: 'Next',
        prevButton: 'Previous',
        seeResult: 'See My Result',
        questionOf: 'Question {{current}} of {{total}}',
        complete: 'Complete',
        questions: {
          style: {
            question: 'Which style matches you most?',
            options: {
              minimalist: 'Scandinavian Minimalist',
              rustic: 'Rustic & Handmade',
              tech: 'Tech & Smart',
              maximalist: 'Nostalgic Maximalist',
            },
          },
          colors: {
            question: 'Which color palette attracts you most?',
            options: {
              neutral: 'Elegant Neutrals',
              traditional: 'Vibrant Traditional',
              earthy: 'Cozy Earthy',
              colorful: 'Colorful Mix',
            },
          },
          materials: {
            question: 'What type of materials do you prefer?',
            options: {
              natural: 'Natural (wood, jute, pinecones)',
              shiny: 'Shiny (crystals, glitter, metals)',
              sustainable: 'Sustainable (recycled, organic)',
              mixed: 'Mix of everything',
            },
          },
          lighting: {
            question: 'How should the lighting be?',
            options: {
              warm: 'Traditional warm lights',
              smart: 'Smart LEDs with app',
              colorful: 'Colorful blinking',
              minimal: 'Minimalist (few lights)',
            },
          },
          tree: {
            question: 'Which type of tree do you prefer?',
            options: {
              natural: 'Natural (real pine)',
              artificialPremium: 'Premium artificial (realistic)',
              artificialSustainable: 'Sustainable artificial (recycled)',
              alternative: 'Creative alternative (branches, wall)',
            },
          },
        },
        profiles: {
          minimalist: {
            name: 'Contemporary Minimalist',
            description: 'You value elegance and simplicity. Less is more, and each element is chosen with intention.',
            colors: 'White, beige, soft gold, charcoal',
            materials: 'Glass, light wood, linen, sustainable materials',
          },
          rustic: {
            name: 'Rustic & Handmade',
            description: 'You connect with nature and tradition. Each ornament tells a story and brings warmth.',
            colors: 'Moss green, terracotta, copper, natural tones',
            materials: 'Jute, pinecones, crochet, felt, dried flowers',
          },
          tech: {
            name: 'Tech & Modern',
            description: 'You love innovation and practicality. Your Christmas combines tradition with the future.',
            colors: 'White, silver, blue, metallic tones',
            materials: 'Smart LEDs, metals, acrylic, modern materials',
          },
          maximalist: {
            name: 'Glamorous Maximalist',
            description: 'You celebrate abundance and nostalgia. More is more, and Christmas is pure joy.',
            colors: 'Red, gold, multicolored, vibrant tones',
            materials: 'Crystals, feathers, glitter, luxury ornaments',
          },
        },
        result: {
          yourProfile: 'Your Christmas Profile 2025',
          freeTips: 'Free Tips',
          colorsLabel: 'Recommended Colors',
          materialsLabel: 'Ideal Materials',
          evaluateNow: 'Rate My Tree Now',
          backHome: 'Back to Home',
          socialProof: '{{count}} people have already discovered their style!',
        },
        premium: {
          title: 'Unlock Complete Guide',
          benefit1: 'Complete 2025 trends guide for {{profile}}',
          benefit2: 'Personalized shopping list',
          benefit3: 'Specific lighting tips',
          benefit4: 'Comparison with rated trees in the app',
          cta: 'Insights Premium',
          evaluations: '{{count}} evaluations',
          subtitle: 'Limited offer for 2025 season',
        },
      },
      camera: {
        back: 'Back',
        capture: 'Capture',
        gallery: 'Gallery',
        retake: 'Retake',
        evaluate: 'Evaluate with AI',
        loginRequired: 'Please login to rate trees',
        cameraError: 'Failed to access camera',
      },
      result: {
        scoreLabel: 'Rate',
        yourScore: 'Your Score',
        shareButton: 'Share',
        upgradeButton: 'Unlock Insights',
        loading: 'Evaluating your tree...',
        description: 'Tree Analysis',
        insights: 'Insights & Trends',
        upgradeMessage: 'Upgrade your plan to access detailed insights and trend analysis',
        premium: {
          title: 'Unlock Complete Analysis',
          benefit1: 'Detailed analysis by category (Lighting, Ornaments, Symmetry, Creativity)',
          benefit2: '2025 trends that match your tree',
          benefit3: 'Specific improvement suggestions',
          benefit4: 'Comparative ranking with other trees',
        },
        storiesPreview: '📱 Preview for Instagram Stories',
        wantFullAnalysis: '🎄 Want to see the full analysis?',
        loginPrompt: 'Login to see detailed description, save your evaluations and unlock premium insights!',
        evaluateAnother: 'Rate Another Tree (Login)',
        shareInstagram: 'Share on Instagram',
        myTree: 'My Christmas Tree',
        shareText: 'My tree scored {{score}}/10! Rate yours at treerated10.com 🎄',
        preparingImage: 'Preparing image...',
        sharedSuccess: 'Shared successfully!',
        imageDownloaded: 'Image downloaded! Share it on Instagram',
        shareError: 'Error sharing',
        waitProcessing: 'Wait for image processing',
        back: 'Back',
        evaluationNotFound: 'Evaluation not found',
        checkoutError: 'Failed to start checkout',
        loginError: 'Please login to view results',
        evaluationError: 'Failed to evaluate tree',
        forcePreview: '🔧 Force Preview Creation (Debug)',
      },
      premiumInsights: {
        title: 'Premium Insights',
        unlocked: 'Unlocked',
        detailedAnalysis: 'Detailed Analysis',
        decorationTrends: 'Decoration Trends',
        improvementSuggestions: 'Improvement Suggestions',
        yourRanking: 'Your Ranking',
        categories: {
          lighting: 'Lighting',
          ornaments: 'Ornaments',
          symmetry: 'Symmetry',
          creativity: 'Creativity',
        },
      },
    },
  },
  es: {
    translation: {
      appName: 'ÁrbolCalificado10',
      landing: {
        title: 'Califica tu Árbol de Navidad con IA',
        subtitle: 'Toma una foto y obtén una calificación instantánea',
        cameraButton: 'Abrir Cámara',
        galleryButton: 'Elegir de Galería',
        quizButton: 'Descubre tu Estilo 2025',
        login: 'Iniciar Sesión',
      },
      howItWorks: {
        title: 'Cómo Funciona',
        subtitle: 'Califica tu árbol de Navidad en 3 simples pasos',
        step1Title: 'Toma una Foto',
        step1Desc: 'Usa tu cámara o elige de la galería',
        step2Title: 'IA Analiza',
        step2Desc: 'Nuestra IA evalúa decoración, iluminación y tendencias',
        step3Title: 'Obtén tu Puntuación',
        step3Desc: 'Recibe calificación y consejos personalizados',
      },
      trends: {
        badge: 'TENDENCIAS 2025',
        title: 'Descubre tu Estilo de Navidad',
        subtitle: '¿Qué tendencia 2025 combina con tu personalidad?',
        minimalist: 'Minimalista',
        rustic: 'Rústico',
        tech: 'Tecnológico',
        glam: 'Glamuroso',
        cta: 'Hacer el Quiz de Estilo',
        learnMore: 'Más información sobre las tendencias 2025',
      },
      trendsPage: {
        meta: {
          title: 'Tendencias de Decoración de Árbol de Navidad 2025 - Guía Completa | TreeRated10',
          description: 'Descubre las principales tendencias de decoración de árbol de Navidad para 2025: minimalismo escandinavo, estilo rústico, tecnología inteligente y maximalismo glamuroso.',
          keywords: 'tendencias árbol navidad 2025, decoración navideña, minimalismo escandinavo, navidad rústica, luces led inteligentes',
        },
        header: { back: 'Volver' },
        hero: {
          badge: 'TENDENCIAS 2025',
          title: 'Guía Completa de Decoración de Árbol de Navidad 2025',
          subtitle: 'Descubre los estilos, colores y materiales que dominarán esta temporada',
          readTime: '📖 Tiempo de lectura: 8 minutos',
        },
        toc: { title: '📑 Índice' },
        tips: { title: 'Consejos Prácticos' },
        colors: { neutral: 'Neutro', gold: 'Dorado', green: 'Verde', red: 'Rojo', blue: 'Azul' },
        sections: {
          minimalist: { title: 'Minimalismo Escandinavo', description: 'El minimalismo acogedor toma protagonismo en las decoraciones de 2025. Menos es más: los árboles ganan pocos adornos, pero seleccionados con intención y alto impacto visual.', tips: ['Usa tonos neutros', 'Elige pocos adornos de calidad', 'Luces LED cálidas', 'Materiales naturales', 'Deja espacio negativo'] },
          rustic: { title: 'Estilo Rústico y Artesanal', description: 'Fuerte tendencia de decoración rústica y artesanal, alineada con la búsqueda de conexiones con la naturaleza y la sostenibilidad.', tips: ['Elementos naturales', 'Lazos de yute', 'Adornos hechos a mano', 'Madera y tonos tierra', 'Piezas únicas'] },
          tech: { title: 'Decoración Tecnológica', description: 'La innovación tecnológica marca presencia en los árboles de Navidad de 2025, de forma elegante y funcional.', tips: ['Luces LED inteligentes', 'Controla colores por app', 'Sincroniza con música', 'Velas electrónicas', 'Control por voz'] },
          glam: { title: 'Maximalismo Nostálgico', description: 'Movimiento de maximalismo nostálgico que abraza el "más es más" con alegría.', tips: ['Mezcla colores vibrantes', 'Llena el árbol', 'Luces multicolor', 'Lazos grandes', 'Cristales y brillos'] },
          colors: { title: 'Colores Predominantes 2025', description: 'Las paletas de colores navideñas en 2025 reflejan la dualidad de tendencias.', tips: ['Combina neutros con metálicos', 'Rojo y verde tradicional', 'Azul y plata elegante', 'Tonos tierra acogedores', 'Tonos pastel modernos'] },
          materials: { title: 'Materiales e Iluminación LED', description: 'La elección de materiales está influenciada por la búsqueda de calidad y sostenibilidad.', sustainable: { title: 'Materiales Sostenibles', items: ['Vidrio soplado', 'Cerámica', 'Fibras naturales', 'Madera reciclada', 'Metales'] }, lighting: { title: 'Iluminación LED Inteligente', items: ['Cadenas LED WiFi', 'Colores programables', 'Sincronización musical', 'Temporizadores', 'Bajo consumo'] } },
          naturalVsArtificial: { title: 'Árboles Naturales vs Artificiales', description: 'En 2025, los árboles artificiales continúan predominando en la preferencia del público.', natural: { title: 'Árbol Natural', prosLabel: 'Ventajas', pros: ['Fragancia auténtica', 'Experiencia completa', 'Apariencia natural', 'Biodegradable'], consLabel: 'Desventajas', cons: ['Requiere riego', 'Pierde agujas', 'Costo anual', 'Disponibilidad limitada'] }, artificial: { title: 'Árbol Artificial', prosLabel: 'Ventajas', pros: ['Reutilizable años', 'Sin mantenimiento', 'Economía a largo plazo', 'Varios tamaños'], consLabel: 'Desventajas', cons: ['Impacto fabricación', 'Sin fragancia', 'Requiere almacenamiento', 'Usar 10+ años'] } },
        },
        cta: { title: '¿Listo para Descubrir tu Estilo?', subtitle: 'Haz nuestro quiz y descubre qué tendencia 2025 combina con tu personalidad', quiz: 'Descubre tu Estilo', evaluate: 'Califica tu Árbol' },
      },
      socialProof: {
        trusted: 'Confiado por entusiastas de la Navidad',
        title: 'Únete a miles calificando sus árboles',
        treesRated: 'Árboles Calificados',
        avgRating: 'Calificación Promedio',
        satisfaction: 'Satisfacción del Usuario',
      },
      faq: {
        title: 'Preguntas Frecuentes',
        q1: '¿Cómo funciona la evaluación con IA?',
        a1: 'Nuestra IA analiza tu foto del árbol de Navidad considerando iluminación, adornos, simetría, creatividad y tendencias 2025. Recibes una puntuación instantánea de 0-10 con feedback personalizado.',
        q2: '¿Es gratis usarlo?',
        a2: '¡Sí! Obtienes una evaluación gratis para probar el servicio. Después, puedes crear una cuenta gratuita para más evaluaciones o actualizar a premium para insights detallados y análisis de tendencias.',
        q3: '¿Qué son los insights premium?',
        a3: 'Los insights premium incluyen análisis detallado por categoría (iluminación, adornos, simetría, creatividad), tendencias 2025 que combinan con tu árbol, sugerencias específicas de mejora y ranking comparativo con otros árboles.',
        q4: '¿Puedo compartir mi calificación?',
        a4: '¡Absolutamente! Después de la evaluación, puedes compartir tu puntuación en Instagram Stories, WhatsApp y otras redes sociales con una hermosa imagen personalizada.',
        q5: '¿Cuáles son las tendencias de Navidad 2025?',
        a5: 'Las principales tendencias 2025 son: Minimalista Contemporáneo (tonos neutros, materiales sostenibles), Rústico y Artesanal (elementos naturales, artesanías), Tecnológico y Smart (luces LED con control por app), y Maximalista Glamuroso (colores vibrantes, adornos de lujo).',
      },
      quiz: {
        title: 'Descubre tu Estilo de Navidad 2025',
        subtitle: 'Responde 5 preguntas y descubre las tendencias perfectas para ti',
        back: 'Volver',
        nextButton: 'Siguiente',
        prevButton: 'Anterior',
        seeResult: 'Ver Mi Resultado',
        questionOf: 'Pregunta {{current}} de {{total}}',
        complete: 'Completo',
        questions: {
          style: {
            question: '¿Qué estilo te representa más?',
            options: {
              minimalist: 'Minimalista Escandinavo',
              rustic: 'Rústico y Artesanal',
              tech: 'Tecnológico y Smart',
              maximalist: 'Maximalista Nostálgico',
            },
          },
          colors: {
            question: '¿Qué paleta de colores te atrae más?',
            options: {
              neutral: 'Neutrales Elegantes',
              traditional: 'Tradicionales Vibrantes',
              earthy: 'Terrosos Acogedores',
              colorful: 'Mix Colorido',
            },
          },
          materials: {
            question: '¿Qué tipo de materiales prefieres?',
            options: {
              natural: 'Naturales (madera, yute, piñas)',
              shiny: 'Brillantes (cristales, purpurina, metales)',
              sustainable: 'Sostenibles (reciclados, orgánicos)',
              mixed: 'Mezcla de todo',
            },
          },
          lighting: {
            question: '¿Cómo debe ser la iluminación?',
            options: {
              warm: 'Luces cálidas tradicionales',
              smart: 'LEDs inteligentes con app',
              colorful: 'Parpadeantes coloridas',
              minimal: 'Minimalista (pocas luces)',
            },
          },
          tree: {
            question: '¿Qué tipo de árbol prefieres?',
            options: {
              natural: 'Natural (pino real)',
              artificialPremium: 'Artificial premium (realista)',
              artificialSustainable: 'Artificial sostenible (reciclado)',
              alternative: 'Alternativa creativa (ramas, pared)',
            },
          },
        },
        profiles: {
          minimalist: {
            name: 'Minimalista Contemporáneo',
            description: 'Valoras la elegancia y la simplicidad. Menos es más, y cada elemento se elige con intención.',
            colors: 'Blanco, beige, dorado suave, carbón',
            materials: 'Vidrio, madera clara, lino, materiales sostenibles',
          },
          rustic: {
            name: 'Rústico y Artesanal',
            description: 'Te conectas con la naturaleza y la tradición. Cada adorno cuenta una historia y aporta calidez.',
            colors: 'Verde musgo, terracota, cobre, tonos naturales',
            materials: 'Yute, piñas, crochet, fieltro, flores secas',
          },
          tech: {
            name: 'Tecnológico y Moderno',
            description: 'Amas la innovación y la practicidad. Tu Navidad combina tradición con futuro.',
            colors: 'Blanco, plata, azul, tonos metálicos',
            materials: 'LEDs inteligentes, metales, acrílico, materiales modernos',
          },
          maximalist: {
            name: 'Maximalista Glamuroso',
            description: 'Celebras la abundancia y la nostalgia. Más es más, y la Navidad es pura alegría.',
            colors: 'Rojo, dorado, multicolor, tonos vibrantes',
            materials: 'Cristales, plumas, purpurina, adornos de lujo',
          },
        },
        result: {
          yourProfile: 'Tu Perfil de Navidad 2025',
          freeTips: 'Consejos Gratis',
          colorsLabel: 'Colores Recomendados',
          materialsLabel: 'Materiales Ideales',
          evaluateNow: 'Evaluar Mi Árbol Ahora',
          backHome: 'Volver al Inicio',
          socialProof: '¡{{count}} personas ya descubrieron su estilo!',
        },
        premium: {
          title: 'Desbloquear Guía Completa',
          benefit1: 'Guía completa de tendencias 2025 para {{profile}}',
          benefit2: 'Lista de compras personalizada',
          benefit3: 'Consejos de iluminación específicos',
          benefit4: 'Comparación con árboles evaluados en la app',
          cta: 'Insights Premium',
          evaluations: '{{count}} evaluaciones',
          subtitle: 'Oferta limitada temporada 2025',
        },
      },
      camera: {
        back: 'Volver',
        capture: 'Capturar',
        gallery: 'Galería',
        retake: 'Repetir',
        evaluate: 'Evaluar con IA',
        loginRequired: 'Por favor inicia sesión para evaluar árboles',
        cameraError: 'Error al acceder a la cámara',
      },
      result: {
        scoreLabel: 'Puntuación',
        yourScore: 'Tu Puntuación',
        shareButton: 'Compartir',
        upgradeButton: 'Desbloquear Insights',
        loading: 'Evaluando tu árbol...',
        description: 'Análisis del Árbol',
        insights: 'Insights y Tendencias',
        upgradeMessage: 'Actualiza tu plan para acceder a análisis detallados y tendencias',
        premium: {
          title: 'Desbloquear Análisis Completo',
          benefit1: 'Análisis detallado por categoría (Iluminación, Adornos, Simetría, Creatividad)',
          benefit2: 'Tendencias 2025 que combinan con tu árbol',
          benefit3: 'Sugerencias específicas de mejora',
          benefit4: 'Ranking comparativo con otros árboles',
        },
        storiesPreview: '📱 Vista previa para Instagram Stories',
        wantFullAnalysis: '🎄 ¿Quieres ver el análisis completo?',
        loginPrompt: '¡Inicia sesión para ver la descripción detallada, guardar tus evaluaciones y desbloquear insights premium!',
        evaluateAnother: 'Evaluar Otro Árbol (Iniciar Sesión)',
        shareInstagram: 'Compartir en Instagram',
        myTree: 'Mi Árbol de Navidad',
        shareText: '¡Mi árbol sacó {{score}}/10! Evalúa el tuyo en treerated10.com 🎄',
        preparingImage: 'Preparando imagen...',
        sharedSuccess: '¡Compartido con éxito!',
        imageDownloaded: '¡Imagen descargada! Compártela en Instagram',
        shareError: 'Error al compartir',
        waitProcessing: 'Espera el procesamiento de la imagen',
        back: 'Volver',
        evaluationNotFound: 'Evaluación no encontrada',
        checkoutError: 'Error al iniciar el pago',
        loginError: 'Por favor inicia sesión para ver resultados',
        evaluationError: 'Error al evaluar el árbol',
        forcePreview: '🔧 Forzar Creación de Vista Previa (Debug)',
      },
      premiumInsights: {
        title: 'Insights Premium',
        unlocked: 'Desbloqueado',
        detailedAnalysis: 'Análisis Detallado',
        decorationTrends: 'Tendencias de Decoración',
        improvementSuggestions: 'Sugerencias de Mejora',
        yourRanking: 'Tu Clasificación',
        categories: {
          lighting: 'Iluminación',
          ornaments: 'Adornos',
          symmetry: 'Simetría',
          creativity: 'Creatividad',
        },
      },
    },
  },
  pt: {
    translation: {
      appName: 'ÁrvoreAvaliada10',
      landing: {
        title: 'Avalie sua Árvore de Natal com IA',
        subtitle: 'Tire uma foto e receba uma avaliação instantânea',
        cameraButton: 'Avaliar Minha Árvore',
        galleryButton: 'Escolher da Galeria',
        quizButton: 'Descubra seu Estilo 2025',
        login: 'Entrar',
      },
      howItWorks: {
        title: 'Como Funciona',
        subtitle: 'Avalie sua árvore de Natal em 3 passos simples',
        step1Title: 'Tire uma Foto',
        step1Desc: 'Use sua câmera ou escolha da galeria',
        step2Title: 'IA Analisa',
        step2Desc: 'Nossa IA avalia decoração, iluminação e tendências',
        step3Title: 'Receba sua Nota',
        step3Desc: 'Receba avaliação e dicas personalizadas',
      },
      trends: {
        badge: 'TENDÊNCIAS 2025',
        title: 'Descubra seu Estilo de Natal',
        subtitle: 'Qual tendência 2025 combina com sua personalidade?',
        minimalist: 'Minimalista',
        rustic: 'Rústico',
        tech: 'Tecnológico',
        glam: 'Glamuroso',
        cta: 'Fazer o Quiz de Estilo',
        learnMore: 'Saiba mais sobre as tendências 2025',
      },
      trendsPage: {
        meta: {
          title: 'Tendências de Decoração de Árvore de Natal 2025 - Guia Completo | Árvore Nota 10',
          description: 'Descubra as principais tendências de decoração de árvore de Natal para 2025: minimalismo escandinavo, estilo rústico, tecnologia inteligente e maximalismo glamuroso.',
          keywords: 'tendências árvore natal 2025, decoração natal, minimalismo escandinavo, natal rústico, luzes led inteligentes',
        },
        header: { back: 'Voltar' },
        hero: {
          badge: 'TENDÊNCIAS 2025',
          title: 'Guia Completo de Decoração de Árvore de Natal 2025',
          subtitle: 'Descubra os estilos, cores e materiais que vão dominar esta temporada',
          readTime: '📖 Tempo de leitura: 8 minutos',
        },
        toc: { title: '📑 Índice' },
        tips: { title: 'Dicas Práticas' },
        colors: { neutral: 'Neutro', gold: 'Dourado', green: 'Verde', red: 'Vermelho', blue: 'Azul' },
        sections: {
          minimalist: { title: 'Minimalismo Escandinavo', description: 'O minimalismo aconchegante assume papel central nas decorações de 2025. Menos é mais: as árvores ganham poucos enfeites, porém selecionados com intenção e alto impacto visual, valorizando espaço vazio e formas simples.', tips: ['Use tons neutros como branco, bege e cinza suave', 'Escolha poucos enfeites de alta qualidade', 'Prefira luzes LED quentes (amareladas)', 'Adicione materiais naturais como madeira clara', 'Deixe espaço negativo - não sobrecarregue'] },
          rustic: { title: 'Estilo Rústico e Artesanal', description: 'Em paralelo ao minimalismo, há forte tendência de decoração rústica e artesanal, alinhada à busca por conexões com a natureza e sustentabilidade. Texturas naturais continuam em alta: enfeites de madeira, pinhas, galhos secos, laços de juta trazem charme rústico e acolhedor.', tips: ['Adicione elementos naturais como pinhas e galhos', 'Use laços de juta, linho e fibras naturais', 'Inclua enfeites artesanais (crochê, macramê, cerâmica)', 'Misture madeira com tons terra e verdes', 'Crie peças únicas para contar sua história'] },
          tech: { title: 'Decoração Tecnológica e Interativa', description: 'A inovação tecnológica também marca presença nas árvores de Natal de 2025, de forma elegante e funcional. As árvores ganharam iluminação LED inteligente, com pisca-piscas controlados por apps e dispositivos smart home.', tips: ['Instale luzes LED inteligentes com controle por app', 'Programe cores e padrões de luz conforme a ocasião', 'Sincronize luzes com músicas de Natal', 'Use velas eletrônicas para segurança', 'Adicione controle por voz via assistentes inteligentes'] },
          glam: { title: 'Maximalismo Nostálgico e Glamour', description: 'Em contraste aos adeptos do "menos é mais", existe um movimento forte de maximalismo nostálgico que abraça o "mais é mais" com alegria. Nessa tendência, a decoração retorna ao exuberante estilo dos anos 80 e 90.', tips: ['Misture cores vibrantes sem medo (vermelho, verde, rosa, roxo)', 'Preencha cada espaço da árvore com enfeites variados', 'Use luzes multicoloridas para efeito festivo', 'Adicione laços grandes e festões', 'Inclua purpurina, cristais e materiais luxuosos'] },
          colors: { title: 'Cores Predominantes em 2025', description: 'As paletas de cores natalinas em 2025 refletem essa dualidade de tendências. De um lado, tons neutros e terrosos ganham destaque nas propostas contemporâneas e minimalistas. Por outro lado, há renascimento de paletas coloridas e vibrantes.', tips: ['Experimente combinar tons neutros com acentos metálicos', 'Se gosta de cor, misture vermelho e verde tradicional com toques modernos', 'Azul e prata criam efeito gelado elegante', 'Tons terrosos (terracota, ferrugem) trazem vibe rústica aconchegante', 'Tons pastel adicionam toque moderno suave'] },
          materials: { title: 'Materiais e Iluminação LED', description: 'A escolha de materiais para árvores e enfeites em 2025 está fortemente influenciada pela busca por qualidade, sustentabilidade e estética refinada. Em vez de plástico descartável, consumidores optam por materiais duráveis e ecologicamente corretos.', sustainable: { title: 'Materiais Sustentáveis', items: ['Vidro soprado ou pintado à mão', 'Cerâmica e porcelana', 'Fibras naturais: juta, linho, algodão, feltro', 'Madeira reciclada ou recuperada', 'Metais: cobre, latão (infinitamente recicláveis)'] }, lighting: { title: 'Iluminação LED Inteligente', items: ['Cordões LED com WiFi/Bluetooth', 'Cores e efeitos programáveis', 'Sincronização com música', 'Temporizadores automáticos e controle remoto', 'Baixo consumo de energia e seguro (não esquenta)'] } },
          naturalVsArtificial: { title: 'Árvores Naturais vs Artificiais', description: 'Uma pergunta que sempre surge a cada ano é: árvore natural ou artificial? Em 2025, observa-se que as árvores de Natal artificiais continuam predominando na preferência do público, porém com novas preocupações guiando essa escolha.', natural: { title: 'Árvore Natural', prosLabel: 'Vantagens', pros: ['Fragrância autêntica de pinheiro fresco', 'Experiência completa de Natal', 'Aparência natural e única', 'Biodegradável e compostável'], consLabel: 'Desvantagens', cons: ['Requer rega e cuidados', 'Solta agulhas', 'Custo anual mais alto', 'Disponibilidade limitada em algumas regiões'] }, artificial: { title: 'Árvore Artificial', prosLabel: 'Vantagens', pros: ['Reutilizável por muitos anos', 'Sem necessidade de manutenção', 'Economia a longo prazo', 'Disponível em vários tamanhos e estilos', 'Algumas feitas de materiais reciclados'], consLabel: 'Desvantagens', cons: ['Impacto inicial de fabricação', 'Sem fragrância natural', 'Requer armazenamento adequado', 'Precisa ser usada 10+ anos para compensar impacto ambiental'] } },
        },
        cta: { title: 'Pronto para Descobrir Seu Estilo?', subtitle: 'Faça nosso quiz e descubra qual tendência 2025 combina com sua personalidade', quiz: 'Descubra Seu Estilo', evaluate: 'Avalie Sua Árvore' },
      },
      socialProof: {
        trusted: 'Confiado por entusiastas do Natal',
        title: 'Junte-se a milhares avaliando suas árvores',
        treesRated: 'Árvores Avaliadas',
        avgRating: 'Avaliação Média',
        satisfaction: 'Satisfação do Usuário',
      },
      faq: {
        title: 'Perguntas Frequentes',
        q1: 'Como funciona a avaliação com IA?',
        a1: 'Nossa IA analisa sua foto da árvore de Natal considerando iluminação, enfeites, simetria, criatividade e tendências 2025. Você recebe uma nota instantânea de 0-10 com feedback personalizado.',
        q2: 'É grátis para usar?',
        a2: 'Sim! Você ganha uma avaliação grátis para testar o serviço. Depois, pode criar uma conta gratuita para mais avaliações ou fazer upgrade para premium para insights detalhados e análise de tendências.',
        q3: 'O que são insights premium?',
        a3: 'Insights premium incluem análise detalhada por categoria (iluminação, enfeites, simetria, criatividade), tendências 2025 que combinam com sua árvore, sugestões específicas de melhoria e ranking comparativo com outras árvores.',
        q4: 'Posso compartilhar minha avaliação?',
        a4: 'Com certeza! Após a avaliação, você pode compartilhar sua nota no Instagram Stories, WhatsApp e outras redes sociais com uma linda imagem personalizada.',
        q5: 'Quais são as tendências de Natal 2025?',
        a5: 'As principais tendências 2025 são: Minimalista Contemporâneo (tons neutros, materiais sustentáveis), Rústico & Artesanal (elementos naturais, artesanato), Tecnológico & Moderno (luzes LED com controle por app), e Maximalista Glamuroso (cores vibrantes, enfeites luxuosos).',
      },
      quiz: {
        title: 'Descubra seu Estilo de Natal 2025',
        subtitle: 'Responda 5 perguntas e descubra as tendências perfeitas para você',
        back: 'Voltar',
        nextButton: 'Próxima',
        prevButton: 'Anterior',
        seeResult: 'Ver Meu Resultado',
        questionOf: 'Pergunta {{current}} de {{total}}',
        complete: 'Completo',
        questions: {
          style: {
            question: 'Qual estilo combina mais com você?',
            options: {
              minimalist: 'Minimalista Escandinavo',
              rustic: 'Rústico e Artesanal',
              tech: 'Tecnológico e Smart',
              maximalist: 'Maximalista Nostálgico',
            },
          },
          colors: {
            question: 'Qual paleta de cores te atrai mais?',
            options: {
              neutral: 'Neutros Elegantes',
              traditional: 'Tradicionais Vibrantes',
              earthy: 'Terrosos Aconchegantes',
              colorful: 'Mix Colorido',
            },
          },
          materials: {
            question: 'Que tipo de materiais você prefere?',
            options: {
              natural: 'Naturais (madeira, juta, pinhas)',
              shiny: 'Brilhantes (cristais, purpurina, metais)',
              sustainable: 'Sustentáveis (reciclados, orgânicos)',
              mixed: 'Mix de tudo',
            },
          },
          lighting: {
            question: 'Como deve ser a iluminação?',
            options: {
              warm: 'Luzes quentes tradicionais',
              smart: 'LEDs inteligentes com app',
              colorful: 'Pisca-pisca colorido',
              minimal: 'Minimalista (poucas luzes)',
            },
          },
          tree: {
            question: 'Qual tipo de árvore você prefere?',
            options: {
              natural: 'Natural (pinheiro real)',
              artificialPremium: 'Artificial premium (visual realista)',
              artificialSustainable: 'Artificial sustentável (reciclada)',
              alternative: 'Alternativa criativa (galhos, parede)',
            },
          },
        },
        profiles: {
          minimalist: {
            name: 'Minimalista Contemporâneo',
            description: 'Você valoriza elegância e simplicidade. Menos é mais, e cada elemento é escolhido com intenção.',
            colors: 'Branco, bege, dourado suave, carvão',
            materials: 'Vidro, madeira clara, linho, materiais sustentáveis',
          },
          rustic: {
            name: 'Rústico & Artesanal',
            description: 'Você se conecta com a natureza e a tradição. Cada enfeite conta uma história e traz aconchego.',
            colors: 'Verde musgo, terracota, cobre, tons naturais',
            materials: 'Juta, pinhas, crochê, feltro, flores secas',
          },
          tech: {
            name: 'Tecnológico & Moderno',
            description: 'Você ama inovação e praticidade. Seu Natal combina tradição com futuro.',
            colors: 'Branco, prata, azul, tons metálicos',
            materials: 'LEDs inteligentes, metais, acrílico, materiais modernos',
          },
          maximalist: {
            name: 'Maximalista Glamouroso',
            description: 'Você celebra a abundância e a nostalgia. Mais é mais, e o Natal é pura alegria.',
            colors: 'Vermelho, dourado, multicolorido, tons vibrantes',
            materials: 'Cristais, plumas, purpurina, enfeites de luxo',
          },
        },
        result: {
          yourProfile: 'Seu Perfil de Natal 2025',
          freeTips: 'Dicas Grátis',
          colorsLabel: 'Cores Recomendadas',
          materialsLabel: 'Materiais Ideais',
          evaluateNow: 'Avaliar Minha Árvore Agora',
          backHome: 'Voltar ao Início',
          socialProof: '{{count}} pessoas já descobriram seu estilo!',
        },
        premium: {
          title: 'Desbloquear Guia Completo',
          benefit1: 'Guia completo de tendências 2025 para {{profile}}',
          benefit2: 'Lista de compras personalizada',
          benefit3: 'Dicas de iluminação específicas',
          benefit4: 'Comparação com árvores avaliadas no app',
          cta: 'Insights Premium',
          evaluations: '{{count}} avaliações',
          subtitle: 'Oferta limitada temporada 2025',
        },
      },
      camera: {
        back: 'Voltar',
        capture: 'Capturar',
        gallery: 'Galeria',
        retake: 'Tirar Novamente',
        evaluate: 'Avaliar com IA',
        loginRequired: 'Por favor faça login para avaliar árvores',
        cameraError: 'Falha ao acessar câmera',
      },
      result: {
        scoreLabel: 'Nota',
        yourScore: 'Sua Pontuação',
        shareButton: 'Compartilhar',
        upgradeButton: 'Desbloquear Insights',
        loading: 'Avaliando sua árvore...',
        description: 'Análise da Árvore',
        insights: 'Insights e Tendências',
        upgradeMessage: 'Faça upgrade do seu plano para acessar insights detalhados e análise de tendências',
        premium: {
          title: 'Desbloquear Análise Completa',
          benefit1: 'Análise detalhada por categoria (Iluminação, Ornamentos, Simetria, Criatividade)',
          benefit2: 'Tendências 2025 que combinam com sua árvore',
          benefit3: 'Sugestões específicas de melhoria',
          benefit4: 'Ranking comparativo com outras árvores',
        },
        storiesPreview: '📱 Preview para Instagram Stories',
        wantFullAnalysis: '🎄 Quer ver a análise completa?',
        loginPrompt: 'Faça login para ver a descrição detalhada, salvar suas avaliações e desbloquear insights premium!',
        evaluateAnother: 'Avaliar Outra Árvore (Login)',
        shareInstagram: 'Compartilhar no Instagram',
        myTree: 'Minha Árvore de Natal',
        shareText: 'Minha árvore tirou {{score}}/10! Avalie a sua em treerated10.com 🎄',
        preparingImage: 'Preparando imagem...',
        sharedSuccess: 'Compartilhado com sucesso!',
        imageDownloaded: 'Imagem baixada! Compartilhe no Instagram',
        shareError: 'Erro ao compartilhar',
        waitProcessing: 'Aguarde o processamento da imagem',
        back: 'Voltar',
        evaluationNotFound: 'Avaliação não encontrada',
        checkoutError: 'Falha ao iniciar checkout',
        loginError: 'Por favor faça login para ver resultados',
        evaluationError: 'Falha ao avaliar árvore',
        forcePreview: '🔧 Forçar Criação de Preview (Debug)',
      },
      premiumInsights: {
        title: 'Insights Premium',
        unlocked: 'Desbloqueado',
        detailedAnalysis: 'Análise Detalhada',
        decorationTrends: 'Tendências de Decoração',
        improvementSuggestions: 'Sugestões de Melhoria',
        yourRanking: 'Sua Classificação',
        categories: {
          lighting: 'Iluminação',
          ornaments: 'Ornamentos',
          symmetry: 'Simetria',
          creativity: 'Criatividade',
        },
      },
    },
  },
  it: {
    translation: {
      appName: 'AlberoVoto10',
      landing: {
        title: 'Valuta il Tuo Albero di Natale con l\'IA',
        subtitle: 'Scatta una foto e ottieni una valutazione istantanea',
        cameraButton: 'Apri Fotocamera',
        galleryButton: 'Scegli dalla Galleria',
        quizButton: 'Scopri il Tuo Stile 2025',
        login: 'Accedi',
      },
      howItWorks: {
        title: 'Come Funziona',
        subtitle: 'Valuta il tuo albero di Natale in 3 semplici passaggi',
        step1Title: 'Scatta una Foto',
        step1Desc: 'Usa la fotocamera o scegli dalla galleria',
        step2Title: 'L\'IA Analizza',
        step2Desc: 'La nostra IA valuta decorazioni, illuminazione e tendenze',
        step3Title: 'Ottieni il Tuo Punteggio',
        step3Desc: 'Ricevi una valutazione e consigli personalizzati',
      },
      trends: {
        badge: 'TENDENZE 2025',
        title: 'Scopri il Tuo Stile Natalizio',
        subtitle: 'Quale tendenza 2025 combacia con la tua personalità?',
        minimalist: 'Minimalista',
        rustic: 'Rustico',
        tech: 'Tecnologico',
        glam: 'Glamour',
        cta: 'Fai il Quiz di Stile',
        learnMore: 'Scopri di più sulle tendenze 2025',
      },
      trendsPage: {
        meta: {
          title: 'Tendenze Decorazione Albero di Natale 2025 - Guida Completa | AlberoVoto10',
          description: 'Scopri le principali tendenze di decorazione dell\'albero di Natale per il 2025: minimalismo scandinavo, stile rustico, tecnologia intelligente e massimalismo glamour.',
          keywords: 'tendenze albero natale 2025, decorazione natalizia, minimalismo scandinavo, natale rustico, luci led intelligenti',
        },
        header: { back: 'Indietro' },
        hero: {
          badge: 'TENDENZE 2025',
          title: 'Guida Completa alla Decorazione dell\'Albero di Natale 2025',
          subtitle: 'Scopri gli stili, i colori e i materiali che domineranno questa stagione',
          readTime: '📖 Tempo di lettura: 8 minuti',
        },
        toc: { title: '📑 Indice' },
        tips: { title: 'Consigli Pratici' },
        colors: { neutral: 'Neutro', gold: 'Oro', green: 'Verde', red: 'Rosso', blue: 'Blu' },
        sections: {
          minimalist: { title: 'Minimalismo Scandinavo', description: 'Il minimalismo accogliente assume un ruolo centrale nelle decorazioni del 2025. Meno è più: gli alberi hanno pochi ornamenti, ma scelti con intenzione e alto impatto visivo, valorizzando spazi vuoti e forme semplici.', tips: ['Usa toni neutri come bianco, beige e grigio tenue', 'Scegli pochi ornamenti di alta qualità', 'Preferisci luci LED calde (giallastre)', 'Aggiungi materiali naturali come legno chiaro', 'Lascia spazio negativo - non sovraccaricare'] },
          rustic: { title: 'Stile Rustico e Artigianale', description: 'Parallelo al minimalismo, c\'è una forte tendenza verso la decorazione rustica e artigianale, allineata alla ricerca di connessioni con la natura e la sostenibilità. Texture naturali continuano a salire: ornamenti in legno, pigne, rami secchi, fiocchi di juta portano fascino rustico e accogliente.', tips: ['Aggiungi elementi naturali come pigne e rami', 'Usa fiocchi di juta, lino e fibre naturali', 'Includi ornamenti artigianali (uncinetto, macramè, ceramica)', 'Mescola legno con toni terra e verdi', 'Crea pezzi unici per raccontare la tua storia'] },
          tech: { title: 'Decorazione Tecnologica e Interattiva', description: 'L\'innovazione tecnologica segna anche la presenza negli alberi di Natale del 2025, in modo elegante e funzionale. Gli alberi hanno guadagnato illuminazione LED intelligente, con luci controllate da app e dispositivi smart home.', tips: ['Installa luci LED intelligenti con controllo via app', 'Programma colori e pattern di luce secondo l\'occasione', 'Sincronizza le luci con musica natalizia', 'Usa candele elettroniche per sicurezza', 'Aggiungi controllo vocale tramite assistenti intelligenti'] },
          glam: { title: 'Massimalismo Nostalgico e Glamour', description: 'In contrasto con gli adepti del "meno è più", c\'è un forte movimento di massimalismo nostalgico che abbraccia il "più è più" con gioia. In questa tendenza, la decorazione ritorna allo stile esuberante degli anni \'80 e \'90.', tips: ['Mescola colori vivaci senza paura (rosso, verde, rosa, viola)', 'Riempi ogni spazio dell\'albero con ornamenti vari', 'Usa luci multicolori per effetto festivo', 'Aggiungi grandi fiocchi e ghirlande', 'Includi glitter, cristalli e materiali lussuosi'] },
          colors: { title: 'Colori Predominanti nel 2025', description: 'Le palette di colori natalizi nel 2025 riflettono questa dualità di tendenze. Da un lato, toni neutri e terrosi guadagnano risalto nelle proposte contemporanee e minimaliste. Dall\'altro, c\'è una rinascita di palette colorate e vibranti.', tips: ['Prova a combinare toni neutri con accenti metallici', 'Se ami il colore, mescola rosso e verde tradizionali con tocchi moderni', 'Blu e argento creano un elegante effetto ghiacciato', 'Toni terrosi (terracotta, ruggine) portano atmosfera rustica accogliente', 'Toni pastello aggiungono un tocco moderno delicato'] },
          materials: { title: 'Materiali e Illuminazione LED', description: 'La scelta dei materiali per alberi e ornamenti nel 2025 è fortemente influenzata dalla ricerca di qualità, sostenibilità ed estetica raffinata. Invece di plastica usa e getta, i consumatori optano per materiali durevoli ed ecologicamente corretti.', sustainable: { title: 'Materiali Sostenibili', items: ['Vetro soffiato o dipinto a mano', 'Ceramica e porcellana', 'Fibre naturali: juta, lino, cotone, feltro', 'Legno riciclato o recuperato', 'Metalli: rame, ottone (infinitamente riciclabili)'] }, lighting: { title: 'Illuminazione LED Intelligente', items: ['Catene LED con WiFi/Bluetooth', 'Colori ed effetti programmabili', 'Sincronizzazione con musica', 'Timer automatici e telecomando', 'Basso consumo energetico e sicuro (non scalda)'] } },
          naturalVsArtificial: { title: 'Alberi Naturali vs Artificiali', description: 'Una domanda che sorge sempre ogni anno è: albero naturale o artificiale? Nel 2025, si osserva che gli alberi di Natale artificiali continuano a predominare nella preferenza del pubblico, ma con nuove preoccupazioni che guidano questa scelta.', natural: { title: 'Albero Naturale', prosLabel: 'Vantaggi', pros: ['Fragranza autentica di pino fresco', 'Esperienza completa di Natale', 'Aspetto naturale e unico', 'Biodegradabile e compostabile'], consLabel: 'Svantaggi', cons: ['Richiede irrigazione e cure', 'Perde aghi', 'Costo annuale più alto', 'Disponibilità limitata in alcune regioni'] }, artificial: { title: 'Albero Artificiale', prosLabel: 'Vantaggi', pros: ['Riutilizzabile per molti anni', 'Nessuna manutenzione necessaria', 'Economia a lungo termine', 'Disponibile in varie dimensioni e stili', 'Alcuni fatti di materiali riciclati'], consLabel: 'Svantaggi', cons: ['Impatto iniziale di produzione', 'Senza fragranza naturale', 'Richiede conservazione adeguata', 'Necessita di essere usato 10+ anni per compensare impatto ambientale'] } },
        },
        cta: { title: 'Pronto a Scoprire il Tuo Stile?', subtitle: 'Fai il nostro quiz e scopri quale tendenza 2025 si adatta alla tua personalità', quiz: 'Scopri il Tuo Stile', evaluate: 'Valuta il Tuo Albero' },
      },
      socialProof: {
        trusted: 'Fidato dagli appassionati di Natale',
        title: 'Unisciti a migliaia che valutano i loro alberi',
        treesRated: 'Alberi Valutati',
        avgRating: 'Valutazione Media',
        satisfaction: 'Soddisfazione Utenti',
      },
      faq: {
        title: 'Domande Frequenti',
        q1: 'Come funziona la valutazione con IA?',
        a1: 'La nostra IA analizza la foto del tuo albero di Natale considerando illuminazione, decorazioni, simmetria, creatività e tendenze 2025. Ricevi un punteggio istantaneo da 0-10 con feedback personalizzato.',
        q2: 'È gratuito?',
        a2: 'Sì! Ottieni una valutazione gratuita per provare il servizio. Dopo, puoi creare un account gratuito per più valutazioni o aggiornare a premium per insights dettagliati e analisi delle tendenze.',
        q3: 'Cosa sono gli insights premium?',
        a3: 'Gli insights premium includono analisi dettagliata per categoria (illuminazione, decorazioni, simmetria, creatività), tendenze 2025 che combinano con il tuo albero, suggerimenti specifici di miglioramento e ranking comparativo con altri alberi.',
        q4: 'Posso condividere la valutazione del mio albero?',
        a4: 'Assolutamente! Dopo la valutazione, puoi condividere il punteggio del tuo albero su Instagram Stories, WhatsApp e altri social network con una bellissima immagine personalizzata.',
        q5: 'Quali sono le tendenze natalizie 2025?',
        a5: 'Le principali tendenze 2025 sono: Minimalista Contemporaneo (toni neutri, materiali sostenibili), Rustico e Artigianale (elementi naturali, artigianato), Tech e Smart (luci LED con controllo app), e Maximalista Glamour (colori vibranti, decorazioni di lusso).',
      },
      quiz: {
        title: 'Scopri il Tuo Stile Natalizio 2025',
        subtitle: 'Rispondi a 5 domande e scopri le tendenze perfette per te',
        back: 'Indietro',
        nextButton: 'Avanti',
        prevButton: 'Precedente',
        seeResult: 'Vedi il Mio Risultato',
        questionOf: 'Domanda {{current}} di {{total}}',
        complete: 'Completo',
        questions: {
          style: {
            question: 'Quale stile ti rappresenta di più?',
            options: {
              minimalist: 'Minimalista Scandinavo',
              rustic: 'Rustico e Artigianale',
              tech: 'Tecnologico e Smart',
              maximalist: 'Maximalista Nostalgico',
            },
          },
          colors: {
            question: 'Quale palette di colori ti attira di più?',
            options: {
              neutral: 'Neutri Eleganti',
              traditional: 'Tradizionali Vibranti',
              earthy: 'Terrosi Accoglienti',
              colorful: 'Mix Colorato',
            },
          },
          materials: {
            question: 'Che tipo di materiali preferisci?',
            options: {
              natural: 'Naturali (legno, juta, pigne)',
              shiny: 'Brillanti (cristalli, glitter, metalli)',
              sustainable: 'Sostenibili (riciclati, organici)',
              mixed: 'Mix di tutto',
            },
          },
          lighting: {
            question: 'Come dovrebbe essere l\'illuminazione?',
            options: {
              warm: 'Luci calde tradizionali',
              smart: 'LED intelligenti con app',
              colorful: 'Colorate lampeggianti',
              minimal: 'Minimalista (poche luci)',
            },
          },
          tree: {
            question: 'Quale tipo di albero preferisci?',
            options: {
              natural: 'Naturale (pino vero)',
              artificialPremium: 'Artificiale premium (realistico)',
              artificialSustainable: 'Artificiale sostenibile (riciclato)',
              alternative: 'Alternativa creativa (rami, parete)',
            },
          },
        },
        profiles: {
          minimalist: {
            name: 'Minimalista Contemporaneo',
            description: 'Valorizzi eleganza e semplicità. Meno è più, e ogni elemento è scelto con intenzione.',
            colors: 'Bianco, beige, oro tenue, carbone',
            materials: 'Vetro, legno chiaro, lino, materiali sostenibili',
          },
          rustic: {
            name: 'Rustico e Artigianale',
            description: 'Ti connetti con la natura e la tradizione. Ogni decorazione racconta una storia e porta calore.',
            colors: 'Verde muschio, terracotta, rame, toni naturali',
            materials: 'Juta, pigne, uncinetto, feltro, fiori secchi',
          },
          tech: {
            name: 'Tecnologico e Moderno',
            description: 'Ami innovazione e praticità. Il tuo Natale combina tradizione con futuro.',
            colors: 'Bianco, argento, blu, toni metallici',
            materials: 'LED intelligenti, metalli, acrilico, materiali moderni',
          },
          maximalist: {
            name: 'Maximalista Glamour',
            description: 'Celebri abbondanza e nostalgia. Più è più, e il Natale è pura gioia.',
            colors: 'Rosso, oro, multicolore, toni vibranti',
            materials: 'Cristalli, piume, glitter, decorazioni di lusso',
          },
        },
        result: {
          yourProfile: 'Il Tuo Profilo Natalizio 2025',
          freeTips: 'Consigli Gratuiti',
          colorsLabel: 'Colori Consigliati',
          materialsLabel: 'Materiali Ideali',
          evaluateNow: 'Valuta il Mio Albero Ora',
          backHome: 'Torna alla Home',
          socialProof: '{{count}} persone hanno già scoperto il loro stile!',
        },
        premium: {
          title: 'Sblocca la Guida Completa',
          benefit1: 'Guida completa tendenze 2025 per {{profile}}',
          benefit2: 'Lista della spesa personalizzata',
          benefit3: 'Consigli specifici per l\'illuminazione',
          benefit4: 'Confronto con alberi valutati nell\'app',
          cta: 'Insights Premium',
          evaluations: '{{count}} valutazioni',
          subtitle: 'Offerta limitata stagione 2025',
        },
      },
      camera: {
        back: 'Indietro',
        capture: 'Scatta',
        gallery: 'Galleria',
        retake: 'Riscatta',
        evaluate: 'Valuta con IA',
        loginRequired: 'Effettua il login per valutare gli alberi',
        cameraError: 'Impossibile accedere alla fotocamera',
      },
      result: {
        scoreLabel: 'Valutazione',
        yourScore: 'Il Tuo Punteggio',
        shareButton: 'Condividi',
        upgradeButton: 'Sblocca Insights',
        loading: 'Valutando il tuo albero...',
        description: 'Analisi dell\'Albero',
        insights: 'Insights e Tendenze',
        upgradeMessage: 'Aggiorna il tuo piano per accedere a insights dettagliati e analisi delle tendenze',
        premium: {
          title: 'Sblocca Analisi Completa',
          benefit1: 'Analisi dettagliata per categoria (Illuminazione, Decorazioni, Simmetria, Creatività)',
          benefit2: 'Tendenze 2025 che combinano con il tuo albero',
          benefit3: 'Suggerimenti specifici di miglioramento',
          benefit4: 'Ranking comparativo con altri alberi',
        },
        storiesPreview: '📱 Anteprima per Instagram Stories',
        wantFullAnalysis: '🎄 Vuoi vedere l\'analisi completa?',
        loginPrompt: 'Accedi per vedere la descrizione dettagliata, salvare le tue valutazioni e sbloccare insights premium!',
        evaluateAnother: 'Valuta un Altro Albero (Login)',
        shareInstagram: 'Condividi su Instagram',
        myTree: 'Il Mio Albero di Natale',
        shareText: 'Il mio albero ha ottenuto {{score}}/10! Valuta il tuo su treerated10.com 🎄',
        preparingImage: 'Preparando immagine...',
        sharedSuccess: 'Condiviso con successo!',
        imageDownloaded: 'Immagine scaricata! Condividila su Instagram',
        shareError: 'Errore nella condivisione',
        waitProcessing: 'Attendi l\'elaborazione dell\'immagine',
        back: 'Indietro',
        evaluationNotFound: 'Valutazione non trovata',
        checkoutError: 'Impossibile avviare il checkout',
        loginError: 'Effettua il login per vedere i risultati',
        evaluationError: 'Impossibile valutare l\'albero',
        forcePreview: '🔧 Forza Creazione Anteprima (Debug)',
      },
      premiumInsights: {
        title: 'Insights Premium',
        unlocked: 'Sbloccato',
        detailedAnalysis: 'Analisi Dettagliata',
        decorationTrends: 'Tendenze Decorative',
        improvementSuggestions: 'Suggerimenti di Miglioramento',
        yourRanking: 'La Tua Classifica',
        categories: {
          lighting: 'Illuminazione',
          ornaments: 'Decorazioni',
          symmetry: 'Simmetria',
          creativity: 'Creatività',
        },
      },
      auth: {
        signIn: 'Accedi',
        signUp: 'Registrati',
        signOut: 'Esci',
        email: 'Email',
        password: 'Password',
        forgotPassword: 'Password Dimenticata?',
        noAccount: 'Non hai un account?',
        hasAccount: 'Hai già un account?',
        resetPassword: 'Reimposta Password',
        sendResetLink: 'Invia Link di Reimpostazione',
        backToSignIn: 'Torna al Login',
        checkEmail: 'Controlla la tua email per il link di reimpostazione della password',
      },
      history: {
        title: 'Cronologia Valutazioni',
        noEvaluations: 'Nessuna valutazione ancora',
        noEvaluationsDesc: 'Inizia a valutare i tuoi alberi di Natale!',
        viewDetails: 'Vedi Dettagli',
        score: 'Punteggio',
        date: 'Data',
      },
      payment: {
        success: 'Pagamento Riuscito!',
        successMessage: 'Grazie per il tuo acquisto. I tuoi insights premium sono stati sbloccati!',
        canceled: 'Pagamento Annullato',
        canceledMessage: 'Il tuo pagamento è stato annullato. Nessun addebito è stato effettuato.',
        returnHome: 'Torna alla Home',
      },
    },
  },
  de: {
    translation: {
      appName: 'BaumBewertet10',
      landing: {
        title: 'Bewerte Deinen Weihnachtsbaum mit KI',
        subtitle: 'Mach ein Foto und erhalte eine sofortige Bewertung',
        cameraButton: 'Kamera Öffnen',
        galleryButton: 'Aus Galerie Wählen',
        quizButton: 'Entdecke Deinen Stil 2025',
        login: 'Anmelden',
      },
      howItWorks: {
        title: 'So Funktioniert\'s',
        subtitle: 'Bewerte deinen Weihnachtsbaum in 3 einfachen Schritten',
        step1Title: 'Foto Machen',
        step1Desc: 'Nutze deine Kamera oder wähle aus der Galerie',
        step2Title: 'KI Analysiert',
        step2Desc: 'Unsere KI bewertet Dekoration, Beleuchtung und Trends',
        step3Title: 'Erhalte Deine Punktzahl',
        step3Desc: 'Erhalte eine Bewertung und personalisierte Tipps',
      },
      trends: {
        badge: 'TRENDS 2025',
        title: 'Entdecke Deinen Weihnachtsstil',
        subtitle: 'Welcher 2025 Trend passt zu deiner Persönlichkeit?',
        minimalist: 'Minimalistisch',
        rustic: 'Rustikal',
        tech: 'Technologisch',
        glam: 'Glamourös',
        cta: 'Mach das Stil-Quiz',
        learnMore: 'Mehr über die Trends 2025 erfahren',
      },
      trendsPage: {
        meta: {
          title: 'Weihnachtsbaum-Dekorationstrends 2025 - Vollständiger Leitfaden | BaumBewertet10',
          description: 'Entdecke die wichtigsten Weihnachtsbaum-Dekorationstrends für 2025: skandinavischer Minimalismus, rustikaler Stil, intelligente Technologie und glamouröser Maximalismus.',
          keywords: 'weihnachtsbaum trends 2025, weihnachtsdekoration, skandinavischer minimalismus, rustikale weihnachten, intelligente led lichter',
        },
        header: { back: 'Zurück' },
        hero: {
          badge: 'TRENDS 2025',
          title: 'Vollständiger Leitfaden zur Weihnachtsbaum-Dekoration 2025',
          subtitle: 'Entdecke die Stile, Farben und Materialien, die diese Saison dominieren werden',
          readTime: '📖 Lesezeit: 8 Minuten',
        },
        toc: { title: '📑 Inhaltsverzeichnis' },
        tips: { title: 'Praktische Tipps' },
        colors: { neutral: 'Neutral', gold: 'Gold', green: 'Grün', red: 'Rot', blue: 'Blau' },
        sections: {
          minimalist: { title: 'Skandinavischer Minimalismus', description: 'Gemütlicher Minimalismus nimmt eine zentrale Rolle in den Dekorationen 2025 ein. Weniger ist mehr: Bäume erhalten wenige Ornamente, aber mit Absicht ausgewählt und hoher visueller Wirkung, wodurch leere Räume und einfache Formen geschätzt werden.', tips: ['Verwende neutrale Töne wie Weiß, Beige und sanftes Grau', 'Wähle wenige hochwertige Ornamente', 'Bevorzuge warme LED-Lichter (gelblich)', 'Füge natürliche Materialien wie helles Holz hinzu', 'Lasse negativen Raum - nicht überladen'] },
          rustic: { title: 'Rustikaler und Handgefertigter Stil', description: 'Parallel zum Minimalismus gibt es einen starken Trend zu rustikaler und handwerklicher Dekoration, ausgerichtet auf die Suche nach Verbindungen mit der Natur und Nachhaltigkeit. Natürliche Texturen bleiben im Trend: Holzornamente, Tannenzapfen, trockene Zweige, Jutebögen bringen rustikalen und gemütlichen Charme.', tips: ['Füge natürliche Elemente wie Tannenzapfen und Zweige hinzu', 'Verwende Jutebögen, Leinen und Naturfasern', 'Integriere handgefertigte Ornamente (Häkeln, Makramee, Keramik)', 'Mische Holz mit erdigen und grünen Tönen', 'Erstelle einzigartige Stücke, um deine Geschichte zu erzählen'] },
          tech: { title: 'Technologische und Interaktive Dekoration', description: 'Technologische Innovation markiert auch die Präsenz in Weihnachtsbäumen 2025, elegant und funktional. Bäume haben intelligente LED-Beleuchtung gewonnen, mit Lichtern, die über Apps und Smart-Home-Geräte gesteuert werden.', tips: ['Installiere intelligente LED-Lichter mit App-Steuerung', 'Programmiere Farben und Lichtmuster je nach Anlass', 'Synchronisiere Lichter mit Weihnachtsmusik', 'Verwende elektronische Kerzen für Sicherheit', 'Füge Sprachsteuerung über intelligente Assistenten hinzu'] },
          glam: { title: 'Nostalgischer Maximalismus und Glamour', description: 'Im Gegensatz zu den Anhängern von "weniger ist mehr" gibt es eine starke Bewegung des nostalgischen Maximalismus, der "mehr ist mehr" mit Freude umarmt. In diesem Trend kehrt die Dekoration zum üppigen Stil der 80er und 90er Jahre zurück.', tips: ['Mische lebendige Farben ohne Angst (Rot, Grün, Rosa, Lila)', 'Fülle jeden Raum des Baumes mit verschiedenen Ornamenten', 'Verwende mehrfarbige Lichter für festliche Wirkung', 'Füge große Schleifen und Girlanden hinzu', 'Integriere Glitzer, Kristalle und luxuriöse Materialien'] },
          colors: { title: 'Vorherrschende Farben 2025', description: 'Weihnachtsfarbpaletten in 2025 spiegeln diese Dualität der Trends wider. Einerseits gewinnen neutrale und erdige Töne in zeitgenössischen und minimalistischen Vorschlägen an Bedeutung. Andererseits gibt es eine Wiedergeburt farbenfroher und lebendiger Paletten.', tips: ['Versuche neutrale Töne mit metallischen Akzenten zu kombinieren', 'Wenn du Farbe liebst, mische traditionelles Rot und Grün mit modernen Akzenten', 'Blau und Silber schaffen eleganten eisigen Effekt', 'Erdtöne (Terrakotta, Rost) bringen gemütliche rustikale Atmosphäre', 'Pastelltöne fügen sanfte moderne Note hinzu'] },
          materials: { title: 'Materialien und LED-Beleuchtung', description: 'Die Wahl der Materialien für Bäume und Ornamente in 2025 ist stark von der Suche nach Qualität, Nachhaltigkeit und raffinierter Ästhetik beeinflusst. Anstelle von Einwegplastik entscheiden sich Verbraucher für langlebige und umweltfreundliche Materialien.', sustainable: { title: 'Nachhaltige Materialien', items: ['Mundgeblasenes oder handbemaltes Glas', 'Keramik und Porzellan', 'Natürliche Fasern: Jute, Leinen, Baumwolle, Filz', 'Recyceltes oder wiedergewonnenes Holz', 'Metalle: Kupfer, Messing (unendlich recycelbar)'] }, lighting: { title: 'Intelligente LED-Beleuchtung', items: ['LED-Ketten mit WiFi/Bluetooth', 'Programmierbare Farben und Effekte', 'Synchronisation mit Musik', 'Automatische Timer und Fernbedienung', 'Niedriger Energieverbrauch und sicher (wird nicht heiß)'] } },
          naturalVsArtificial: { title: 'Natürliche vs Künstliche Bäume', description: 'Eine Frage, die jedes Jahr auftaucht, ist: natürlicher oder künstlicher Baum? Im Jahr 2025 wird beobachtet, dass künstliche Weihnachtsbäume weiterhin in der öffentlichen Präferenz vorherrschen, aber mit neuen Bedenken, die diese Wahl leiten.', natural: { title: 'Natürlicher Baum', prosLabel: 'Vorteile', pros: ['Authentischer Duft von frischem Tannenbaum', 'Vollständiges Weihnachtserlebnis', 'Natürliches und einzigartiges Aussehen', 'Biologisch abbaubar und kompostierbar'], consLabel: 'Nachteile', cons: ['Erfordert Bewässerung und Pflege', 'Verliert Nadeln', 'Höhere jährliche Kosten', 'Begrenzte Verfügbarkeit in einigen Regionen'] }, artificial: { title: 'Künstlicher Baum', prosLabel: 'Vorteile', pros: ['Viele Jahre wiederverwendbar', 'Keine Wartung erforderlich', 'Langfristige Wirtschaftlichkeit', 'In verschiedenen Größen und Stilen erhältlich', 'Einige aus recycelten Materialien hergestellt'], consLabel: 'Nachteile', cons: ['Anfängliche Herstellungsauswirkung', 'Kein natürlicher Duft', 'Erfordert angemessene Lagerung', 'Muss 10+ Jahre verwendet werden, um Umweltauswirkungen auszugleichen'] } },
        },
        cta: { title: 'Bereit, Deinen Stil zu Entdecken?', subtitle: 'Mache unser Quiz und finde heraus, welcher 2025 Trend zu deiner Persönlichkeit passt', quiz: 'Entdecke Deinen Stil', evaluate: 'Bewerte Deinen Baum' },
      },
      socialProof: {
        trusted: 'Vertraut von Weihnachtsliebhabern',
        title: 'Schließe dich Tausenden an, die ihre Bäume bewerten',
        treesRated: 'Bewertete Bäume',
        avgRating: 'Durchschnittsbewertung',
        satisfaction: 'Nutzerzufriedenheit',
      },
      faq: {
        title: 'Häufig Gestellte Fragen',
        q1: 'Wie funktioniert die KI-Bewertung?',
        a1: 'Unsere KI analysiert dein Weihnachtsbaumfoto unter Berücksichtigung von Beleuchtung, Dekoration, Symmetrie, Kreativität und 2025 Trends. Du erhältst eine sofortige Punktzahl von 0-10 mit personalisiertem Feedback.',
        q2: 'Ist es kostenlos?',
        a2: 'Ja! Du erhältst eine kostenlose Bewertung zum Ausprobieren. Danach kannst du ein kostenloses Konto für mehr Bewertungen erstellen oder auf Premium upgraden für detaillierte Einblicke und Trendanalyse.',
        q3: 'Was sind Premium-Einblicke?',
        a3: 'Premium-Einblicke beinhalten detaillierte Analyse nach Kategorie (Beleuchtung, Dekoration, Symmetrie, Kreativität), 2025 Trends die zu deinem Baum passen, spezifische Verbesserungsvorschläge und vergleichendes Ranking mit anderen Bäumen.',
        q4: 'Kann ich meine Baumbewertung teilen?',
        a4: 'Auf jeden Fall! Nach der Bewertung kannst du deine Baumpunktzahl auf Instagram Stories, WhatsApp und anderen sozialen Netzwerken mit einem schönen personalisierten Bild teilen.',
        q5: 'Was sind die Weihnachtstrends 2025?',
        a5: 'Die Haupttrends 2025 sind: Zeitgenössisch Minimalistisch (neutrale Töne, nachhaltige Materialien), Rustikal & Handgemacht (natürliche Elemente, Handwerk), Tech & Smart (LED-Lichter mit App-Steuerung), und Glamourös Maximalistisch (lebendige Farben, Luxusornamente).',
      },
      quiz: {
        title: 'Entdecke Deinen Weihnachtsstil 2025',
        subtitle: 'Beantworte 5 Fragen und entdecke die perfekten Trends für dich',
        back: 'Zurück',
        nextButton: 'Weiter',
        prevButton: 'Zurück',
        seeResult: 'Mein Ergebnis Sehen',
        questionOf: 'Frage {{current}} von {{total}}',
        complete: 'Fertig',
        questions: {
          style: {
            question: 'Welcher Stil passt am besten zu dir?',
            options: {
              minimalist: 'Skandinavisch Minimalistisch',
              rustic: 'Rustikal & Handgemacht',
              tech: 'Tech & Smart',
              maximalist: 'Nostalgisch Maximalistisch',
            },
          },
          colors: {
            question: 'Welche Farbpalette spricht dich am meisten an?',
            options: {
              neutral: 'Elegante Neutrale',
              traditional: 'Lebendige Traditionelle',
              earthy: 'Gemütliche Erdtöne',
              colorful: 'Bunter Mix',
            },
          },
          materials: {
            question: 'Welche Art von Materialien bevorzugst du?',
            options: {
              natural: 'Natürlich (Holz, Jute, Tannenzapfen)',
              shiny: 'Glänzend (Kristalle, Glitzer, Metalle)',
              sustainable: 'Nachhaltig (recycelt, organisch)',
              mixed: 'Mix von allem',
            },
          },
          lighting: {
            question: 'Wie soll die Beleuchtung sein?',
            options: {
              warm: 'Traditionelle warme Lichter',
              smart: 'Smart-LEDs mit App',
              colorful: 'Bunte blinkende',
              minimal: 'Minimalistisch (wenige Lichter)',
            },
          },
          tree: {
            question: 'Welche Art von Baum bevorzugst du?',
            options: {
              natural: 'Natürlich (echter Tannenbaum)',
              artificialPremium: 'Premium künstlich (realistisch)',
              artificialSustainable: 'Nachhaltig künstlich (recycelt)',
              alternative: 'Kreative Alternative (Zweige, Wand)',
            },
          },
        },
        profiles: {
          minimalist: {
            name: 'Zeitgenössisch Minimalistisch',
            description: 'Du schätzt Eleganz und Einfachheit. Weniger ist mehr, und jedes Element wird mit Absicht gewählt.',
            colors: 'Weiß, Beige, sanftes Gold, Anthrazit',
            materials: 'Glas, helles Holz, Leinen, nachhaltige Materialien',
          },
          rustic: {
            name: 'Rustikal & Handgemacht',
            description: 'Du verbindest dich mit Natur und Tradition. Jede Dekoration erzählt eine Geschichte und bringt Wärme.',
            colors: 'Moosgrün, Terrakotta, Kupfer, natürliche Töne',
            materials: 'Jute, Tannenzapfen, Häkelware, Filz, Trockenblumen',
          },
          tech: {
            name: 'Tech & Modern',
            description: 'Du liebst Innovation und Praktikabilität. Dein Weihnachten verbindet Tradition mit Zukunft.',
            colors: 'Weiß, Silber, Blau, metallische Töne',
            materials: 'Smart-LEDs, Metalle, Acryl, moderne Materialien',
          },
          maximalist: {
            name: 'Glamourös Maximalistisch',
            description: 'Du feierst Überfluss und Nostalgie. Mehr ist mehr, und Weihnachten ist pure Freude.',
            colors: 'Rot, Gold, mehrfarbig, lebendige Töne',
            materials: 'Kristalle, Federn, Glitzer, Luxusornamente',
          },
        },
        result: {
          yourProfile: 'Dein Weihnachtsprofil 2025',
          freeTips: 'Kostenlose Tipps',
          colorsLabel: 'Empfohlene Farben',
          materialsLabel: 'Ideale Materialien',
          evaluateNow: 'Bewerte Meinen Baum Jetzt',
          backHome: 'Zurück zur Startseite',
          socialProof: '{{count}} Menschen haben bereits ihren Stil entdeckt!',
        },
        premium: {
          title: 'Vollständigen Leitfaden Freischalten',
          benefit1: 'Vollständiger 2025 Trendleitfaden für {{profile}}',
          benefit2: 'Personalisierte Einkaufsliste',
          benefit3: 'Spezifische Beleuchtungstipps',
          benefit4: 'Vergleich mit bewerteten Bäumen in der App',
          cta: 'Premium Einblicke',
          evaluations: '{{count}} Bewertungen',
          subtitle: 'Begrenztes Angebot für die Saison 2025',
        },
      },
      camera: {
        back: 'Zurück',
        capture: 'Aufnehmen',
        gallery: 'Galerie',
        retake: 'Wiederholen',
        evaluate: 'Mit KI Bewerten',
        loginRequired: 'Bitte melde dich an, um Bäume zu bewerten',
        cameraError: 'Kamerazugriff fehlgeschlagen',
      },
      result: {
        scoreLabel: 'Bewertung',
        yourScore: 'Deine Punktzahl',
        shareButton: 'Teilen',
        upgradeButton: 'Einblicke Freischalten',
        loading: 'Bewerte deinen Baum...',
        description: 'Baumanalyse',
        insights: 'Einblicke & Trends',
        upgradeMessage: 'Upgrade deinen Plan für detaillierte Einblicke und Trendanalyse',
        premium: {
          title: 'Vollständige Analyse Freischalten',
          benefit1: 'Detaillierte Analyse nach Kategorie (Beleuchtung, Dekoration, Symmetrie, Kreativität)',
          benefit2: '2025 Trends die zu deinem Baum passen',
          benefit3: 'Spezifische Verbesserungsvorschläge',
          benefit4: 'Vergleichendes Ranking mit anderen Bäumen',
        },
        storiesPreview: '📱 Vorschau für Instagram Stories',
        wantFullAnalysis: '🎄 Möchtest du die vollständige Analyse sehen?',
        loginPrompt: 'Melde dich an, um die detaillierte Beschreibung zu sehen, deine Bewertungen zu speichern und Premium-Einblicke freizuschalten!',
        evaluateAnother: 'Anderen Baum Bewerten (Login)',
        shareInstagram: 'Auf Instagram Teilen',
        myTree: 'Mein Weihnachtsbaum',
        shareText: 'Mein Baum hat {{score}}/10 erreicht! Bewerte deinen auf treerated10.com 🎄',
        preparingImage: 'Bereite Bild vor...',
        sharedSuccess: 'Erfolgreich geteilt!',
        imageDownloaded: 'Bild heruntergeladen! Teile es auf Instagram',
        shareError: 'Fehler beim Teilen',
        waitProcessing: 'Warte auf Bildverarbeitung',
        back: 'Zurück',
        evaluationNotFound: 'Bewertung nicht gefunden',
        checkoutError: 'Checkout konnte nicht gestartet werden',
        loginError: 'Bitte melde dich an, um Ergebnisse zu sehen',
        evaluationError: 'Baum konnte nicht bewertet werden',
        forcePreview: '🔧 Vorschauerstellung Erzwingen (Debug)',
      },
      premiumInsights: {
        title: 'Premium-Einblicke',
        unlocked: 'Freigeschaltet',
        detailedAnalysis: 'Detaillierte Analyse',
        decorationTrends: 'Dekorationstrends',
        improvementSuggestions: 'Verbesserungsvorschläge',
        yourRanking: 'Dein Ranking',
        categories: {
          lighting: 'Beleuchtung',
          ornaments: 'Dekoration',
          symmetry: 'Symmetrie',
          creativity: 'Kreativität',
        },
      },
      auth: {
        signIn: 'Anmelden',
        signUp: 'Registrieren',
        signOut: 'Abmelden',
        email: 'E-Mail',
        password: 'Passwort',
        forgotPassword: 'Passwort Vergessen?',
        noAccount: 'Noch kein Konto?',
        hasAccount: 'Bereits ein Konto?',
        resetPassword: 'Passwort Zurücksetzen',
        sendResetLink: 'Zurücksetzungslink Senden',
        backToSignIn: 'Zurück zur Anmeldung',
        checkEmail: 'Überprüfe deine E-Mail für den Passwort-Zurücksetzungslink',
      },
      history: {
        title: 'Bewertungsverlauf',
        noEvaluations: 'Noch keine Bewertungen',
        noEvaluationsDesc: 'Beginne mit der Bewertung deiner Weihnachtsbäume!',
        viewDetails: 'Details Anzeigen',
        score: 'Punktzahl',
        date: 'Datum',
      },
      payment: {
        success: 'Zahlung Erfolgreich!',
        successMessage: 'Danke für deinen Kauf. Deine Premium-Einblicke wurden freigeschaltet!',
        canceled: 'Zahlung Abgebrochen',
        canceledMessage: 'Deine Zahlung wurde abgebrochen. Es wurden keine Gebühren erhoben.',
        returnHome: 'Zurück zur Startseite',
      },
    },
  },
};

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: navigator.language.split('-')[0] || 'en',
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },
  });

export default i18n;
